import React, {
	memo,
	useCallback,
	useEffect,
	useMemo
} from 'react';
import PropTypes from 'prop-types';
import {
	Accordion,
	AccordionContent,
	AccordionTitle,
	Divider,
	Form,
	Header,
	Icon,
	Message,
	Tab
} from 'semantic-ui-react';

import useFormErrors from './utils/useFormErrors';

import DynamicFormField from './DynamicFormField';
import QuestionNumber from './QuestionNumber';

import styles from './document-modal.module.css';
import FormErrors from '../Shared/FormErrors';

const Questionaire = ( {
	admin,
	formData,
	instructions,
	sections,
	setModuleErrors,
	ui,
	updateField,
	validate
} ) => {
	const {
		clearFormErrors,
		formErrors,
		setFormErrors,
		validateFormErrors,
		handleValidation
	} = useFormErrors();

	useEffect( () => {
		if ( validateFormErrors() ) {
			setModuleErrors( true );
		} else {
			setModuleErrors( false );
		}
	}, [
		setModuleErrors,
		validateFormErrors
	] );

	const questions = useCallback( section => {
		const filteredUi = ui.filter( ( { sectionId } ) => sectionId === section );

		const fieldErrors = [];

		const fields = filteredUi.map( ( field, fieldIndex ) => {
			switch ( field.type ) {
				case 3: { // divider
					return (
						<div
							key={ field.id }
							className={ styles.dynamicFormHeader }
						>
							<Divider hidden />
							{ field.label && <h4>{ field.label }</h4> }
							<p>{ field.description }</p>
							<Divider/>
							<Divider hidden />
						</div>
					);
				}

				case 4: { // group numeric
					const chunks = [];

					// Group elements into sets of 4
					for (
						let i = 0; i < field.options.length; i += 5 ) {
						chunks.push( field.options.slice( i, i + 5 ) );
					}

					return (
						<div
							key={ field.id }
							style={ { marginBottom: '2rem' } }
						>
							<QuestionNumber number={ field.config.numberLabel } />
							<Form.Group style={ { marginBottom: '0' } }>
								<Form.Field required={ field.validation.required }>
									<label>{ field.label }</label>
									<p
										style={ {
											fontSize: '0.9rem',
											marginBottom: '1rem'
										} }
									>
										Please provide a count for each of the categories shown below.
									</p>
								</Form.Field>
							</Form.Group>
							{ chunks.map( ( chunk, i ) => (
								<Form.Group key={ i }>
									{ chunk.map( ( {
										id: qId,
										label
									} ) => {
										const optionId = `${ field.id }_${ qId }`;
										const optionField = {
											...field,
											id: optionId,
											name: optionId,
											label
										};
										const data = formData[ optionId ];
										const errors = handleValidation( {
											data,
											name: optionId,
											label,
											validation: field.validation
										} );

										fieldErrors.push( errors );

										return (
											<DynamicFormField
												key={ optionId }
												data={ formData[ optionId ] }
												errors={ errors }
												field={ { ...optionField, id: optionId } }
												forceValidation={ validate }
												updateField={ updateField }
											/>
										);
									} ) }
								</Form.Group>
							) ) }
						</div>
					);
				}

				default: {
					const {
						label,
						description,
						...formField
					} = field;
					const data = formData[ field.name ];
					const errors = handleValidation( {
						data,
						label: field.label,
						validation: field.validation
					} );

					fieldErrors.push( errors );

					return (
						<div
							key={ field.id }
							style={ { marginBottom: '2rem' } }
						>
							<QuestionNumber number={ field.config.numberLabel } />
							<Form.Group style={ { marginBottom: '0' } }>
								<Form.Field required={ field.validation.required }>
									<label>{ label }</label>
									{ description
										? <p
											style={ {
												fontSize: '0.9rem',
												marginBottom: '1rem'
											} }
											dangerouslySetInnerHTML={ { __html: description } }
										/>
										: null
									}
								</Form.Field>
							</Form.Group>
							<Form.Group key={ fieldIndex }>
								<DynamicFormField
									key={ field.id }
									data={ data }
									errors={ errors }
									field={ formField }
									forceValidation={ validate }
									updateField={ updateField }
								/>
							</Form.Group>
						</div>
					);
				}
			}
		} );

		setFormErrors( fieldErrors );

		return fields;
	}, [
		ui,
		setFormErrors,
		formData,
		handleValidation,
		validate,
		updateField
	] );

	const renderSections = useMemo( () => {
		clearFormErrors();

		const panels = sections.map( ( {
			id,
			name,
			description,
			condition,
			visibility
		} ) => {
			if (
				( visibility === 'admin' && !admin )
				|| ( visibility === 'agency' && admin )
			) {
				return null;
			}

			for ( const { type, id, optionID, value } of condition ) {
				if ( type === 1 ) {
					let key = optionID ? `${ id }_${ optionID }` : id;

					if (
						( Array.isArray( formData[ key ] ) && !formData[ key ].includes( value ) )
						&& formData[ key ] !== value
					) {
						return null;
					}
				}
			}

			return {
				key: id,
				title: <AccordionTitle>
					<Header><Icon name="dropdown" />{ name }</Header>
				</AccordionTitle>,
				content: <AccordionContent>
					<p>{ description }</p>
					{ questions( id ) }
				</AccordionContent>
			};
		} );

		return (
			<>
				{ questions( 0 ) }
				<Accordion
					exclusive={ false }
					fluid
					panels={ panels }
				>

				</Accordion>
			</>
		);
	}, [
		admin,
		clearFormErrors,
		formData,
		questions,
		sections
	] );

	const mapFormErrors = useCallback(
		() => Object.values( formErrors ).map( errors => errors.map( error => error.error
			? <Message.Item key={ error.fieldLabel }>{ error.fieldLabel }</Message.Item>
			: null ) ),
		[ formErrors ]
	);

	return (
		<Tab.Pane
			as="div"
			className={ `${ styles.questionaire } ${ styles.pane }` }
		>
			<div className={ styles.questionaireBorder } />
			<div className={ styles.formContainer }>
				<Form
					autoComplete="off"
					error={ validate && validateFormErrors() }
				>
					{ instructions
						? <div className={ styles.instructions }>
							<h3>Instructions</h3>
							<div dangerouslySetInnerHTML={ { __html: instructions } } />
						</div>
						: null
					}
					{ renderSections }
					<FormErrors mapFormErrors={ mapFormErrors } />
				</Form>
			</div>
		</Tab.Pane>
	);
};

Questionaire.displayName = 'Questionaire';

Questionaire.propTypes = {
	admin: PropTypes.bool,
	formData: PropTypes.object.isRequired,
	instructions: PropTypes.string,
	sections: PropTypes.array.isRequired,
	setModuleErrors: PropTypes.func.isRequired,
	ui: PropTypes.array.isRequired,
	updateField: PropTypes.func.isRequired,
	validate: PropTypes.bool
};

export default memo( Questionaire );
